import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { ReactComponent as BuildingCommunity } from 'assets/icons/BuildingCommunity.svg';
import { ReactComponent as Lock } from 'assets/icons/Lock.svg';
import { ReactComponent as Users } from 'assets/icons/Users.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import i18n from 'i18n/config';
import { filterInviteesToWorkspaceMembers } from 'modules/Messenger/components/InviteMembersField/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';

import { ChatPrivacyEnum } from './ChatPrivacyEnum';
import ChatPrivacyOption from './ChatPrivacyOption';
import { NewChatFormType } from '../CreateNewChatModal';

export { ChatPrivacyEnum };

const SHARED_CHAT_THRESHOLD = workspacesConfig[PermissionId.WorkspaceInviteMembers].powerLevel;

const getPrivacyOptions = () => [
  {
    value: ChatPrivacyEnum.PRIVATE,
    title: i18n.t('messenger.chat_privacy_team_title'),
    description: i18n.t('messenger.chat_privacy_team_description'),
    icon: BuildingCommunity,
  },
  {
    value: ChatPrivacyEnum.PUBLIC,
    title: i18n.t('messenger.chat_privacy_shared_title'),
    description: i18n.t('messenger.chat_privacy_shared_description'),
    icon: Users,
  },
];

const ChatPrivacyField = () => {
  const { control, watch, setValue } = useFormContext<NewChatFormType>();
  const { matrixClient } = useMatrixContext();
  const { workspaceId } = useWorkspacesContext({ requireWorkspaceId: false });
  const { workspace: workspacePowerLevel } = useScopedUserPowerLevels(workspaceId);

  const canCreateSharedChat = !workspaceId || workspacePowerLevel >= SHARED_CHAT_THRESHOLD;
  const chatPrivacy = watch('chatPrivacy');
  const inviteesValue = watch('invitees');
  const invitees = useMemo(() => inviteesValue ?? [], [inviteesValue]);
  const lacksSharedChatPermission = !!workspaceId && workspacePowerLevel < SHARED_CHAT_THRESHOLD;

  useEffect(() => {
    if (!lacksSharedChatPermission || !workspaceId) return;

    if (chatPrivacy === ChatPrivacyEnum.PUBLIC) {
      setValue('chatPrivacy', ChatPrivacyEnum.PRIVATE, { shouldDirty: false });
    }

    const workspaceOnlyInvitees = filterInviteesToWorkspaceMembers(matrixClient, workspaceId, invitees);
    if (workspaceOnlyInvitees.length !== invitees.length) {
      setValue('invitees', workspaceOnlyInvitees, { shouldDirty: false, shouldValidate: true });
    }
  }, [chatPrivacy, invitees, lacksSharedChatPermission, matrixClient, setValue, workspaceId]);

  if (lacksSharedChatPermission) return null;

  const visibleOptions = getPrivacyOptions().filter(
    ({ value }) => value !== ChatPrivacyEnum.PUBLIC || canCreateSharedChat
  );

  return (
    <div className="flex w-full gap-5 items-start">
      <Lock className="stroke-gray-60 h-6 w-6 shrink-0 stroke-1.5" />
      <Controller
        name="chatPrivacy"
        control={control}
        render={({ field: { value, onChange } }) => (
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            {visibleOptions.map(({ value: optionValue, title, description, icon }) => (
              <ChatPrivacyOption
                key={optionValue}
                value={optionValue}
                title={title}
                description={description}
                icon={icon}
                checked={value === optionValue}
                onClick={() => onChange(optionValue)}
              />
            ))}
          </div>
        )}
      />
    </div>
  );
};

export default ChatPrivacyField;
